/**
 * Presentational appointment form blocks shared across calendar categories and
 * the in-session scheduler. Each block takes only the values it renders so
 * callers can compose them in their own layout.
 */

import { calendarDateFromPickerValue } from '@/lib/dateHelpers';
import {
  ActionIcon,
  Alert,
  Box,
  Button,
  Checkbox,
  Grid,
  Group,
  NumberInput,
  Paper,
  SegmentedControl,
  Select,
  Stack,
  Switch,
  Text,
  Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notify } from '@/lib/notify';
import {
  IconBuilding,
  IconCalendarEvent,
  IconClock,
  IconCopy,
} from '@tabler/icons-react';
import type { ComponentProps, ReactNode } from 'react';
import { TIME_SLOT_OPTIONS } from './providerAvailabilityValidation';
import { RoomSelect } from '@/pages/calendar/components/RoomSelect';
import type { MinuteInterval } from '@/pages/calendar/components/roomSchedulingDemoData';
import type { Equipment } from '@/pages/settings/components/EquipmentManagementSection';
import type { Room } from '@/pages/settings/components/RoomManagementSection';

const BORDER_COLOR = 'var(--mantine-color-gray-5)';
const WEEKDAY_INITIALS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const MODALITY_OPTIONS = [
  { value: 'in-person', label: 'In-person' },
  { value: 'telehealth', label: 'Telehealth' },
  { value: 'hybrid', label: 'Hybrid' },
];

export { MODALITY_OPTIONS };

export type AppointmentModality = 'in-person' | 'telehealth' | 'hybrid';

/** Portal/z-index overrides so dropdowns render above the session sidebar chrome. */
export interface OverlayPortalProps {
  comboboxProps?: ComponentProps<typeof Select>['comboboxProps'];
  popoverProps?: ComponentProps<typeof DatePickerInput>['popoverProps'];
}

// ---------------------------------------------------------------------------
// Date + time
// ---------------------------------------------------------------------------

export interface AppointmentDateTimeFieldsValue {
  date: Date | null;
  startTime: string;
  endTime: string;
}

export interface AppointmentDateTimeFieldsProps extends OverlayPortalProps {
  values: AppointmentDateTimeFieldsValue;
  onChange: (patch: Partial<AppointmentDateTimeFieldsValue>) => void;
  errors?: Partial<Record<keyof AppointmentDateTimeFieldsValue, ReactNode>>;
}

export function AppointmentDateTimeFields({
  values,
  onChange,
  errors,
  comboboxProps,
  popoverProps,
}: AppointmentDateTimeFieldsProps): JSX.Element {
  return (
    <Grid>
      <Grid.Col span={4}>
        <DatePickerInput
          label="Date"
          value={values.date}
          onChange={(value) =>
            onChange({
              date: calendarDateFromPickerValue(value),
            })
          }
          leftSection={<IconCalendarEvent size={16} />}
          error={errors?.date}
          popoverProps={popoverProps}
        />
      </Grid.Col>
      <Grid.Col span={4}>
        <Select
          label="Start time"
          value={values.startTime}
          onChange={(v) => onChange({ startTime: v ?? '9:00 AM' })}
          data={TIME_SLOT_OPTIONS}
          leftSection={<IconClock size={16} />}
          error={errors?.startTime}
          comboboxProps={comboboxProps}
        />
      </Grid.Col>
      <Grid.Col span={4}>
        <Select
          label="End time"
          value={values.endTime}
          onChange={(v) => onChange({ endTime: v ?? '10:00 AM' })}
          data={TIME_SLOT_OPTIONS}
          leftSection={<IconClock size={16} />}
          error={errors?.endTime}
          comboboxProps={comboboxProps}
        />
      </Grid.Col>
    </Grid>
  );
}

// ---------------------------------------------------------------------------
// Recurrence
// ---------------------------------------------------------------------------

export interface AppointmentRecurrenceFieldsValue {
  date: Date | null;
  recurringEnabled: boolean;
  recurringInterval: number;
  recurringTimeframe: string;
  recurringDaysOfWeek: string[];
  recurringEndType: 'occurrences' | 'date';
  recurringEndDate: Date | null;
  recurringOccurrences: number;
}

export interface AppointmentRecurrenceFieldsProps extends OverlayPortalProps {
  values: AppointmentRecurrenceFieldsValue;
  onChange: (patch: Partial<AppointmentRecurrenceFieldsValue>) => void;
  /** Editing an existing event hides the toggle; the series alert/panel drive visibility. */
  hideToggle?: boolean;
  /** Existing recurring series — shows the edit alert and forces the panel open. */
  recurringSeries?: boolean;
}

export function AppointmentRecurrenceFields({
  values,
  onChange,
  hideToggle = false,
  recurringSeries = false,
  comboboxProps,
  popoverProps,
}: AppointmentRecurrenceFieldsProps): JSX.Element {
  const showPanel = values.recurringEnabled || recurringSeries;
  return (
    <>
      {!hideToggle && (
        <Checkbox
          label="Recurring"
          checked={values.recurringEnabled}
          onChange={(e) => {
            const checked = e.currentTarget.checked;
            onChange({
              recurringEnabled: checked,
              ...(checked && values.date
                ? { recurringDaysOfWeek: [String(values.date.getDay())] }
                : {}),
            });
          }}
          size="sm"
          mt="sm"
          mb="sm"
        />
      )}
      {recurringSeries && (
        <Alert color="blue" variant="light" style={{ borderColor: BORDER_COLOR }}>
          <Text size="sm">
            This is a recurring event. You can edit the schedule below or modify just this instance.
          </Text>
        </Alert>
      )}
      {showPanel && (
        <Paper
          withBorder
          p="md"
          style={{ borderColor: BORDER_COLOR, backgroundColor: 'var(--mantine-color-gray-0)' }}
        >
          <Stack gap="sm">
            <Group gap="xs" align="center">
              <Text size="sm" c="gray.7" style={{ minWidth: 60 }}>
                Every
              </Text>
              <NumberInput
                value={values.recurringInterval}
                onChange={(v) => onChange({ recurringInterval: Number(v) || 1 })}
                min={1}
                max={30}
                style={{ width: 70 }}
                size="sm"
              />
              <Select
                value={values.recurringTimeframe}
                onChange={(v) => onChange({ recurringTimeframe: v ?? 'week' })}
                data={[
                  { value: 'week', label: 'week' },
                  { value: 'month', label: 'month' },
                ]}
                style={{ width: 100 }}
                size="sm"
                comboboxProps={comboboxProps}
              />
            </Group>
            {values.recurringTimeframe === 'week' && (
              <Group gap="xs" align="center">
                <Text size="sm" c="gray.7" style={{ minWidth: 60 }}>
                  On day(s)
                </Text>
                <Group gap={4}>
                  {WEEKDAY_INITIALS.map((day, idx) => {
                    const isSelected = values.recurringDaysOfWeek.includes(String(idx));
                    return (
                      <Button
                        key={idx}
                        variant={isSelected ? 'filled' : 'outline'}
                        color="gold"
                        size="sm"
                        onClick={() =>
                          onChange({
                            recurringDaysOfWeek: isSelected
                              ? values.recurringDaysOfWeek.filter((d) => d !== String(idx))
                              : [...values.recurringDaysOfWeek, String(idx)],
                          })
                        }
                        style={{ width: 32, height: 32, padding: 0, minWidth: 32 }}
                      >
                        {day}
                      </Button>
                    );
                  })}
                </Group>
              </Group>
            )}
            <Group gap="xs" align="center">
              <Text size="sm" c="gray.7" style={{ minWidth: 60 }}>
                Ends
              </Text>
              <SegmentedControl
                value={values.recurringEndType}
                onChange={(v) => onChange({ recurringEndType: v as 'occurrences' | 'date' })}
                data={[
                  { label: 'After', value: 'occurrences' },
                  { label: 'On', value: 'date' },
                ]}
                size="sm"
              />
              {values.recurringEndType === 'occurrences' && (
                <Group gap="xs" align="center">
                  <NumberInput
                    value={values.recurringOccurrences}
                    onChange={(v) => onChange({ recurringOccurrences: Number(v) || 1 })}
                    min={1}
                    max={365}
                    size="sm"
                    style={{ width: 70 }}
                  />
                  <Text size="sm" c="gray.7">
                    occurrence(s)
                  </Text>
                </Group>
              )}
              {values.recurringEndType === 'date' && (
                <DatePickerInput
                  value={values.recurringEndDate}
                  onChange={(v) => onChange({ recurringEndDate: calendarDateFromPickerValue(v) })}
                  size="sm"
                  style={{ width: 150 }}
                  minDate={values.date ?? undefined}
                  popoverProps={popoverProps}
                />
              )}
            </Group>
          </Stack>
        </Paper>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Modality / location / room / equipment
// ---------------------------------------------------------------------------

export interface AppointmentLocationFieldsValue {
  date: Date | null;
  startTime: string;
  endTime: string;
  modality: AppointmentModality;
  /** Practice site id, or the `telehealth` sentinel. */
  siteId: string;
  telehealthFromOffice: boolean;
  officeForTelehealth: string;
  roomId: string | null;
  deviceIds: string[];
}

export interface AppointmentLocationSite {
  id: string;
  name: string;
  addressLine1?: string;
  city?: string;
  state?: string;
  hours?: import('@unify/fhir-model/resources/location').LocationWeeklyHours;
}

export interface AppointmentLocationFieldsProps extends OverlayPortalProps {
  values: AppointmentLocationFieldsValue;
  onChange: (patch: Partial<AppointmentLocationFieldsValue>) => void;
  errors?: Partial<Record<'siteId' | 'officeForTelehealth', ReactNode>>;
  sites: AppointmentLocationSite[];
  siteOptions: { value: string; label: string }[];
  firstSiteId: string;
  /** Rooms at the resolved scheduling site; filtered to type internally. */
  rooms: Room[];
  portableDevices: Equipment[];
  fixedInRoom: Equipment[];
  busyByRoom?: Map<string, MinuteInterval[]>;
  busyByDevice?: Map<string, MinuteInterval[]>;
  onViewRoomAvailability?: () => void;
  bookingStart?: Date | null;
  bookingEnd?: Date | null;
}

export function AppointmentLocationFields({
  values,
  onChange,
  errors,
  sites,
  siteOptions,
  firstSiteId,
  rooms,
  portableDevices: _portableDevices,
  fixedInRoom: _fixedInRoom,
  busyByRoom,
  busyByDevice: _busyByDevice,
  onViewRoomAvailability,
  comboboxProps,
  bookingStart = null,
  bookingEnd = null,
}: AppointmentLocationFieldsProps): JSX.Element {
  const atPhysicalSite =
    (values.modality === 'in-person' || values.modality === 'hybrid')
    && values.siteId !== 'telehealth';
  const reservesPhoneBooth = values.modality === 'telehealth' && values.telehealthFromOffice;
  const schedulingSiteId = reservesPhoneBooth ? values.officeForTelehealth : values.siteId;
  const siteHours = sites.find((s) => s.id === schedulingSiteId)?.hours;

  const handleModalityChange = (next: AppointmentModality): void => {
    if (next === 'telehealth') {
      onChange({ modality: next, siteId: 'telehealth', roomId: null, deviceIds: [] });
      return;
    }
    onChange({
      modality: next,
      telehealthFromOffice: false,
      siteId: values.siteId === 'telehealth' ? firstSiteId : values.siteId,
    });
  };

  const copySiteAddress = (): void => {
    const site = sites.find((s) => s.id === values.siteId);
    const address = site
      ? [site.addressLine1, site.city, site.state].filter(Boolean).join(', ')
      : '';
    void navigator.clipboard?.writeText(address).then(() =>
      notify({ title: 'Copied', message: 'Address copied', type: 'success' }),
    );
  };

  const roomAvailabilityLink = onViewRoomAvailability ? (
    <Text
      component="button"
      type="button"
      size="sm"
      c="blue"
      style={{
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        padding: 0,
        textAlign: 'left',
        textDecoration: 'underline',
      }}
      onClick={onViewRoomAvailability}
    >
      View room availability
    </Text>
  ) : null;

  return (
    <Stack gap="sm">
      <Grid>
        <Grid.Col span={values.modality === 'telehealth' ? 12 : 6}>
          <Select
            label="Modality"
            value={values.modality}
            onChange={(v) => handleModalityChange((v ?? 'in-person') as AppointmentModality)}
            data={MODALITY_OPTIONS}
            leftSection={<IconBuilding size={16} />}
            comboboxProps={comboboxProps}
          />
        </Grid.Col>
        {values.modality !== 'telehealth' && (
          <Grid.Col span={6}>
            <Group align="flex-end" gap="xs">
              <Box style={{ flex: 1 }}>
                <Select
                  label="Location"
                  value={values.siteId}
                  onChange={(v) =>
                    onChange({ siteId: v ?? firstSiteId, roomId: null, deviceIds: [] })
                  }
                  data={siteOptions}
                  leftSection={<IconBuilding size={16} />}
                  error={errors?.siteId}
                  comboboxProps={comboboxProps}
                />
              </Box>
              <Tooltip label="Copy address">
                <ActionIcon size="lg" variant="light" color="gray" onClick={copySiteAddress}>
                  <IconCopy size={18} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Grid.Col>
        )}
      </Grid>

      {values.modality === 'telehealth' && (
        <Switch
          label="Taking call from office (reserve a phone booth)"
          checked={values.telehealthFromOffice}
          onChange={(e) =>
            onChange({
              telehealthFromOffice: e.currentTarget.checked,
              ...(e.currentTarget.checked ? {} : { roomId: null, deviceIds: [] }),
            })
          }
        />
      )}
      {reservesPhoneBooth && (
        <Select
          label="Office"
          value={values.officeForTelehealth}
          onChange={(v) =>
            onChange({ officeForTelehealth: v ?? firstSiteId, roomId: null, deviceIds: [] })
          }
          data={siteOptions}
          leftSection={<IconBuilding size={16} />}
          error={errors?.officeForTelehealth}
          comboboxProps={comboboxProps}
        />
      )}

      {atPhysicalSite && (
        <Stack gap="xs">
          <RoomSelect
            rooms={rooms.filter((r) => r.type === 'therapy-room')}
            eventDate={values.date}
            startTimeLabel={values.startTime}
            endTimeLabel={values.endTime}
            bookingStart={bookingStart}
            bookingEnd={bookingEnd}
            siteHours={siteHours}
            value={values.roomId}
            busyIntervals={busyByRoom}
            onChange={(id) => onChange({ roomId: id, deviceIds: [] })}
          />
          {roomAvailabilityLink}
        </Stack>
      )}
      {reservesPhoneBooth && (
        <Stack gap="xs">
          <RoomSelect
            rooms={rooms.filter((r) => r.type === 'phone-booth')}
            eventDate={values.date}
            startTimeLabel={values.startTime}
            endTimeLabel={values.endTime}
            bookingStart={bookingStart}
            bookingEnd={bookingEnd}
            siteHours={siteHours}
            value={values.roomId}
            busyIntervals={busyByRoom}
            onChange={(id) => onChange({ roomId: id })}
          />
          {roomAvailabilityLink}
        </Stack>
      )}
    </Stack>
  );
}
